import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const AuthCallback = ({ onLogin }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Signing in with Google...');
  // The Google session_id is single-use. This exchange must run exactly once,
  // even though onLogin() re-renders App and changes the prop identity.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const handleCallback = async () => {
      // Extract session_id from URL hash fragment
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setStatus('Authentication failed');
        toast.error('Google sign-in failed. Please try again.');
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/google/callback`, {
          session_id: sessionId
        });

        const { token, user, is_new_user } = response.data;
        onLogin(token, user);

        if (is_new_user) {
          toast.success(`Welcome, ${user.full_name || 'friend'}! Your account has been created.`);
        } else {
          toast.success(`Welcome back, ${user.full_name || 'friend'}!`);
        }

        navigate('/dashboard/portal');
      } catch (error) {
        const message = error.response?.data?.detail || 'Google sign-in failed';
        setStatus('Authentication failed');
        toast.error(message);
        setTimeout(() => navigate('/'), 2000);
      }
    };

    handleCallback();
    // Intentionally empty deps: this must run exactly once on mount.
    // eslint-disable-next-line
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1628 100%)' }}>
      <div className="text-center space-y-4">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-cyan-200 text-lg" data-testid="auth-callback-status">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
